import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  Users, Mail, Sparkles, RefreshCw, TrendingUp,
  MessageSquare, FileText, Calendar, Trash2, AlertTriangle, UserCheck,
  Unlock, Lock,
} from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, accent = "default" }) {
  const accents = {
    default: "bg-white border-black/5",
    dark: "bg-[#0a0a0a] text-white border-white/10",
  };
  return (
    <div className={`rounded-[14px] border p-5 ${accents[accent]}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">{label}</div>
        {Icon && <Icon size={14} className="opacity-50" />}
      </div>
      <div className="font-display tabular text-3xl font-extrabold mt-2.5 leading-none">{value}</div>
      {sub && <div className="text-[11px] opacity-60 mt-1.5">{sub}</div>}
    </div>
  );
}

function TopList({ title, rows, valueKey, labelKey }) {
  return (
    <div className="card p-5">
      <div className="font-display font-bold text-[14px] mb-3">{title}</div>
      {rows.length === 0 && <div className="text-[12px] text-neutral-500">No data yet.</div>}
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-[13px]">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-5 h-5 rounded-full bg-black/[0.04] text-[10px] font-bold flex items-center justify-center text-neutral-500 shrink-0">
                {i + 1}
              </div>
              <div className="truncate">{r[labelKey]}</div>
            </div>
            <div className="tabular font-semibold">{r[valueKey]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function UsersTable({ rows, currentUserId, onDelete, onImpersonate, onTogglePaywall, onSetGatingMode, busy, impersonatingId }) {
  const [confirmFor, setConfirmFor] = useState(null);

  return (
    <div className="card overflow-hidden" data-testid="admin-users-table">
      <div className="grid grid-cols-12 px-5 py-3 text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 border-b border-black/5 bg-black/[0.02]">
        <div className="col-span-3">User</div>
        <div className="col-span-2">Joined</div>
        <div className="col-span-2">Last sign-in</div>
        <div className="col-span-1 text-right">Programs</div>
        <div className="col-span-1 text-center">Unlimited Plan A Trip</div>
        <div className="col-span-1 text-center">Gate</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {rows.length === 0 && (
        <div className="px-5 py-8 text-[13px] text-neutral-500 text-center">No users yet.</div>
      )}
      {rows.map((u) => {
        const isMe = u.user_id === currentUserId;
        const isConfirming = confirmFor === u.user_id;
        const isImpersonating = impersonatingId === u.user_id;
        const gatingMode = (u.gating_mode || "referral").toLowerCase();
        const gateInactive = !!u.bypass_paywall;
        return (
          <div
            key={u.user_id}
            className="grid grid-cols-12 items-center px-5 py-3.5 border-b border-black/5 last:border-b-0 text-[13px]"
            data-testid={`admin-user-row-${u.user_id}`}
          >
            <div className="col-span-3 min-w-0">
              <div className="font-semibold truncate">{u.name || "—"}</div>
              <div className="text-[11.5px] text-neutral-500 truncate">{u.email}</div>
              {u.referred_by_partner && (
                <div
                  className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-700"
                  data-testid={`admin-user-partner-${u.user_id}`}
                  title={`Signed up via /r/${u.referred_by_partner.slug}${u.referred_by_partner.status === "archived" ? " · (archived)" : ""}`}
                >
                  {u.referred_by_partner.logo_url && (
                    <img
                      src={`${BACKEND_URL}${u.referred_by_partner.logo_url}`}
                      alt=""
                      className="h-3.5 w-3.5 rounded-sm border border-emerald-200 bg-white object-contain shrink-0"
                    />
                  )}
                  <span className="truncate">Invited by {u.referred_by_partner.name}</span>
                </div>
              )}
              {!u.referred_by_partner && u.referred_by_user && (
                <div
                  className="mt-0.5 text-[10.5px] font-semibold text-amber-700 truncate"
                  data-testid={`admin-user-inviter-${u.user_id}`}
                  title={`Personal invite from ${u.referred_by_user.email}`}
                >
                  Invited by {u.referred_by_user.name || u.referred_by_user.email}
                </div>
              )}
            </div>
            <div className="col-span-2 text-neutral-600 text-[12px]">{fmtDate(u.created_at)}</div>
            <div className="col-span-2 text-neutral-600 text-[12px]">{fmtDate(u.last_session_at)}</div>
            <div className="col-span-1 text-right tabular font-semibold">{u.program_count ?? 0}</div>
            <div className="col-span-1 text-center">
              <button
                type="button"
                onClick={() => onTogglePaywall(u)}
                className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border transition-colors ${
                  u.bypass_paywall
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    : "bg-neutral-50 text-neutral-500 border-neutral-200 hover:bg-neutral-100"
                }`}
                title={u.bypass_paywall ? "Paywall bypassed — click to enable paywall" : "Paywall enforced — click to grant free unlimited access"}
                data-testid={`admin-user-paywall-${u.user_id}`}
              >
                {u.bypass_paywall ? <><Unlock size={10} /> ON</> : <><Lock size={10} /> OFF</>}
              </button>
            </div>
            <div className="col-span-1 text-center">
              <select
                value={gatingMode}
                onChange={(e) => onSetGatingMode(u, e.target.value)}
                className={`text-[11px] font-semibold rounded-md border px-1.5 py-0.5 bg-white ${
                  gateInactive ? "text-neutral-400 border-neutral-200" : "text-neutral-800 border-neutral-300"
                }`}
                title={
                  gateInactive
                    ? "Bypass is ON — this gate will only apply once Bypass is turned OFF"
                    : gatingMode === "subscription"
                      ? "Restricted by active subscription"
                      : "Restricted by qualified referral"
                }
                data-testid={`admin-user-gating-${u.user_id}`}
              >
                <option value="referral">Referral</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>
            <div className="col-span-2 text-right">
              {isMe ? (
                <span className="text-[11px] text-neutral-400">(you)</span>
              ) : isConfirming ? (
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    className="text-[11.5px] px-2 py-1 rounded text-neutral-500 hover:text-black"
                    onClick={() => setConfirmFor(null)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn text-[11.5px] py-1.5 px-2.5"
                    style={{ background: "var(--bad)", color: "#fff" }}
                    onClick={async () => {
                      await onDelete(u.user_id);
                      setConfirmFor(null);
                    }}
                    disabled={busy}
                    data-testid={`admin-user-confirm-delete-${u.user_id}`}
                  >
                    {busy ? <span className="spinner" /> : <Trash2 size={11} />} Delete forever
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-end gap-1">
                  <button
                    className="text-neutral-500 hover:text-black p-1.5 rounded transition-colors inline-flex items-center gap-1 text-[11.5px]"
                    onClick={() => onImpersonate(u.user_id)}
                    disabled={isImpersonating || impersonatingId !== null}
                    title="Sign in as this user"
                    data-testid={`admin-user-impersonate-${u.user_id}`}
                  >
                    {isImpersonating ? <span className="spinner" /> : <UserCheck size={13} />}
                    {isImpersonating ? "Switching…" : "Impersonate"}
                  </button>
                  <button
                    className="text-neutral-400 hover:text-red-700 p-1.5 rounded transition-colors"
                    onClick={() => setConfirmFor(u.user_id)}
                    title="Delete user"
                    data-testid={`admin-user-delete-${u.user_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminAnalytics() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  // overview | users | waitlist — driven by ?tab=… so cross-page nav can land
  // directly on a sub-tab (e.g. /admin?tab=users from the shared tab bar).
  const tabParam = searchParams.get("tab");
  const tab = (tabParam === "users" || tabParam === "waitlist") ? tabParam : "overview";
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [flash, setFlash] = useState("");
  const [paywallConfirm, setPaywallConfirm] = useState(null);  // { user, nextValue } | null
  const [paywallBusy, setPaywallBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      const [a, u, w] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/users"),
        api.get("/admin/waitlist"),
      ]);
      setData(a.data);
      setUsers(u.data || []);
      setWaitlist(w.data || []);
    } catch (e) {
      const msg = e?.response?.status === 403 ? "You don't have admin access." : (e?.message || "load failed");
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  async function deleteUser(userId) {
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
      setFlash(`Deleted ${data.email}: ${Object.entries(data.deleted).filter(([_, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(" · ")}`);
      setTimeout(() => setFlash(""), 6000);
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "delete failed");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function approveWaitlist(entryId) {
    setApprovingId(entryId);
    setErr("");
    try {
      const { data } = await api.post(`/admin/waitlist/${entryId}/approve`);
      setWaitlist((prev) =>
        prev.map((w) => (w.id === entryId ? { ...w, status: "approved" } : w))
      );
      const tail = data.email_sent
        ? "approval email sent"
        : data.already_approved
        ? "(already approved — no email re-sent)"
        : "approved (email send failed — check SMTP config)";
      setFlash(`${data.email}: ${tail}`);
      setTimeout(() => setFlash(""), 6000);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "approve failed");
    } finally {
      setApprovingId(null);
    }
  }

  async function setGatingMode(u, nextMode) {
    const prevMode = (u.gating_mode || "referral").toLowerCase();
    if (nextMode === prevMode) return;
    // Optimistic: flip locally first, roll back on failure.
    setUsers((prev) => prev.map((row) =>
      row.user_id === u.user_id ? { ...row, gating_mode: nextMode } : row,
    ));
    try {
      await api.post(`/admin/users/${u.user_id}/gating-mode`, { gating_mode: nextMode });
      setFlash(`${u.email}: gate set to ${nextMode}`);
      setTimeout(() => setFlash(""), 4000);
    } catch (e) {
      setUsers((prev) => prev.map((row) =>
        row.user_id === u.user_id ? { ...row, gating_mode: prevMode } : row,
      ));
      setErr(e?.response?.data?.detail || e?.message || "Could not change gate");
    }
  }

  async function applyPaywallToggle() {
    if (!paywallConfirm) return;
    const { user: u, nextValue } = paywallConfirm;
    setPaywallBusy(true);
    setErr("");
    try {
      await api.post(`/admin/users/${u.user_id}/paywall`, { bypass_paywall: nextValue });
      setUsers((prev) => prev.map((row) =>
        row.user_id === u.user_id ? { ...row, bypass_paywall: nextValue } : row,
      ));
      setFlash(`${u.email}: paywall bypass ${nextValue ? "ENABLED" : "DISABLED"}`);
      setTimeout(() => setFlash(""), 6000);
      setPaywallConfirm(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not update paywall");
    } finally {
      setPaywallBusy(false);
    }
  }

  async function impersonate(userId) {
    setImpersonatingId(userId);
    setErr("");
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      if (data?.user) {
        setUser(data.user);
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "impersonation failed");
    } finally {
      setImpersonatingId(null);
    }
  }

  if (!user) {
    return <div className="p-12 text-center text-[14px]">Please sign in.</div>;
  }

  return (
    <AdminLayout
      activeTab={tab}
      right={
        <button
          onClick={refresh}
          disabled={loading}
          className="btn btn-ghost text-[12.5px]"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }
    >
      <div data-testid="admin-analytics-page">
        <div className="section-eyebrow">Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Operational dashboard</h1>

        {flash && (
          <div className="mt-4 p-3 border rounded-[10px] text-[12.5px]" style={{ background: "var(--good-bg)", color: "var(--good)", borderColor: "var(--good-border)" }} data-testid="admin-flash">
            {flash}
          </div>
        )}
        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }} data-testid="admin-error">
            {err}
          </div>
        )}

        {/* Overview tab */}
        {tab === "overview" && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <StatCard label="Total users" value={data.users.total}
                sub={`+${data.users.new_today} today · +${data.users.new_week} this week`}
                icon={Users} accent="dark" />
              <StatCard label="Active 7d" value={data.users.active_7d}
                sub={`${data.users.total ? Math.round((data.users.active_7d / data.users.total) * 100) : 0}% of total`}
                icon={TrendingUp} />
              <StatCard label="Waitlist" value={data.waitlist.total}
                sub={`+${data.waitlist.new_today} today · ${data.waitlist.approved} approved`} icon={Mail} />
              <StatCard label="Programs tracked" value={data.programs.total}
                sub={`avg ${data.programs.avg_per_user}/user · max ${data.programs.max_per_user}`} icon={Sparkles} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <StatCard label="Concierge chats today" value={data.concierge.today}
                sub={`${data.concierge.week} this week`} icon={MessageSquare} />
              <StatCard label="AW syncs today" value={data.aw_sync.synced_today}
                sub={`${data.aw_sync.failed_today} failed · ${data.aw_sync.total_accounts} accounts`} icon={RefreshCw} />
              <StatCard label="Demos run today" value={data.demo.today}
                sub={`${data.demo.week} this week`} icon={Calendar} />
              <StatCard label="Briefs generated" value={data.briefs.total}
                sub={`${data.briefs.week} this week`} icon={FileText} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-7">
              <TopList title="Top providers tracked" rows={data.programs.top_providers} valueKey="count" labelKey="code" />
              <TopList title="Top expiring awards" rows={data.top_expiring_awards} valueKey="count" labelKey="name" />
            </div>
            <div className="mt-6 text-[11px] text-neutral-500">
              As of {new Date(data.as_of).toLocaleString()}
            </div>
          </>
        )}

        {/* Users tab */}
        {tab === "users" && (
          <div className="mt-6">
            <div className="flex items-start gap-2 p-3 mb-4 rounded-[10px]" style={{ background: "rgba(146, 64, 14, 0.06)", border: "1px solid rgba(252, 211, 77, 0.4)" }}>
              <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-amber-900 leading-relaxed">
                Deleting a user is <strong>permanent</strong> and cascades to all their programs, awards, briefs, sessions, and connected loyalty credentials. There is no undo.
              </div>
            </div>
            <UsersTable
              rows={users}
              currentUserId={user.user_id}
              onDelete={deleteUser}
              onImpersonate={impersonate}
              onTogglePaywall={(u) => setPaywallConfirm({ user: u, nextValue: !u.bypass_paywall })}
              onSetGatingMode={setGatingMode}
              busy={deleteBusy}
              impersonatingId={impersonatingId}
            />
          </div>
        )}

        {/* Waitlist tab */}
        {tab === "waitlist" && (
          <div className="mt-6 card overflow-hidden" data-testid="admin-waitlist-table">
            <div className="grid grid-cols-12 px-5 py-3 text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 border-b border-black/5 bg-black/[0.02]">
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Notes</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {waitlist.length === 0 && (
              <div className="px-5 py-8 text-[13px] text-neutral-500 text-center">No waitlist signups yet.</div>
            )}
            {waitlist.map((w) => {
              const approved = w.status === "approved";
              const busy = approvingId === w.id;
              return (
                <div key={w.id || w.email} className="grid grid-cols-12 px-5 py-3.5 border-b border-black/5 last:border-b-0 text-[13px] items-center">
                  <div className="col-span-4 truncate font-semibold">{w.email}</div>
                  <div className="col-span-2 text-neutral-600 text-[12px]">{w.source || "—"}</div>
                  <div className="col-span-2 text-neutral-600 text-[12px] truncate">{w.reason || w.notes || "—"}</div>
                  <div className="col-span-2 text-neutral-600 text-[12px]">{fmtDate(w.created_at)}</div>
                  <div className="col-span-2 flex justify-end">
                    {approved ? (
                      <span className="pill pill-good" data-testid={`waitlist-status-${w.id}`}>Approved</span>
                    ) : (
                      <button
                        onClick={() => approveWaitlist(w.id)}
                        disabled={busy || approvingId !== null}
                        className="btn btn-primary text-[12px] py-1.5 px-3 inline-flex items-center gap-1.5"
                        data-testid={`waitlist-approve-${w.id}`}
                      >
                        {busy ? (
                          <>
                            <span className="spinner" /> Approving…
                          </>
                        ) : (
                          "Approve"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loading && !data && (
          <div className="mt-8 text-[13px] text-neutral-500 flex items-center gap-2">
            <span className="spinner" /> Loading…
          </div>
        )}
      </div>
      {paywallConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !paywallBusy && setPaywallConfirm(null)}
          data-testid="admin-paywall-confirm"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className={paywallConfirm.nextValue ? "text-emerald-600 shrink-0 mt-0.5" : "text-amber-600 shrink-0 mt-0.5"} />
              <div>
                <h3 className="font-display text-lg font-bold tracking-tight">
                  {paywallConfirm.nextValue ? "Grant free unlimited access?" : "Re-enable paywall?"}
                </h3>
                <p className="text-[13px] text-neutral-600 mt-1.5 leading-relaxed">
                  {paywallConfirm.nextValue ? (
                    <>
                      <strong>{paywallConfirm.user.email}</strong> will get unlimited Plan-a-Trip
                      searches with no paywall, regardless of subscription status.
                    </>
                  ) : (
                    <>
                      <strong>{paywallConfirm.user.email}</strong> will be subject to the standard
                      paywall (2 free search routes, then must subscribe).
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setPaywallConfirm(null)}
                disabled={paywallBusy}
                className="btn text-[13px] py-1.5 px-3 disabled:opacity-40"
                data-testid="admin-paywall-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyPaywallToggle}
                disabled={paywallBusy}
                className="btn-primary text-[13px] py-1.5 px-4 disabled:opacity-40"
                data-testid="admin-paywall-confirm-btn"
              >
                {paywallBusy ? "Saving…" : (paywallConfirm.nextValue ? "Grant access" : "Re-enable paywall")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
